import React from "react";
import {
  AutoSizer,
  Column,
  Table,
  TableCellRenderer,
  TableHeaderProps
} from "react-virtualized";
import clsx from "clsx";
import { LinearProgress, TableSortLabel } from "@material-ui/core";
import {
  createStyles,
  Theme,
  withStyles,
  WithStyles
} from "@material-ui/core/styles";
import TableCell from "@material-ui/core/TableCell";
import { EmptyList } from "src/apps/commons/dataDisplays";
import { PartnerCategoryFlatNodeWithMapping } from "../types";
import { CategorySortColumn } from "../utils";
import { SortState } from "./ListContainers";

const styles = (_theme: Theme) =>
  createStyles({
    flexContainer: {
      display: "flex",
      alignItems: "center",
      boxSizing: "border-box"
    },
    tableRow: {
      cursor: "pointer"
    },
    tableCell: {
      flex: 1
    },
    maxWidth100: {
      maxWidth: "100%"
    },
    marginAuto: {
      margin: "auto"
    },
    noClick: {
      cursor: "initial"
    },

    level1: {
      fontWeight: 600,
      backgroundColor: "#edf2f7"
    },
    level2: {
      fontWeight: 500,
      backgroundColor: "#f5f9fc"
    },
    level3: {
      fontWeight: 400,
      backgroundColor: "#FFFFFF"
    },
    level3NotMapped: {
      fontWeight: 400,
      backgroundColor: "#e5d9cf"
    }
  });

export type CellParams = { rowData: PartnerCategoryFlatNodeWithMapping };
export type CellDataGetter = (params: CellParams) => unknown;
export type CellRenderer = (params: CellParams) => React.ReactNode;
export type RowGetter = (params: RowInfo) => PartnerCategoryFlatNodeWithMapping;

interface ColumnConfig {
  dataKey: keyof PartnerCategoryFlatNodeWithMapping;
  label: string;
  width: number | ((totalWidth: number) => number);
  align?: "left" | "center" | "right";

  sortKey?: CategorySortColumn;
  cellDataGetter?: CellDataGetter;
  cellRenderer?: CellRenderer;
  cellContentRenderer?: CellRenderer;
}

interface RowInfo {
  index: number;
}

interface Props extends WithStyles<typeof styles> {
  loading?: boolean;
  columns: ColumnConfig[];
  headerHeight: number;
  rowHeight: number;
  rowCount: number;
  rowGetter: (row: RowInfo) => PartnerCategoryFlatNodeWithMapping;

  sortState: SortState;
  setSortState: React.Dispatch<React.SetStateAction<SortState>>;
}

class VirtualizedCategoryTreeTableComponent extends React.PureComponent<Props> {
  cellRenderer: TableCellRenderer = params => {
    const { cellData, columnIndex, rowData } = params;
    const { columns, classes, rowHeight } = this.props;
    const { level, mapping } = rowData as PartnerCategoryFlatNodeWithMapping;

    const colParams = columnIndex != null ? columns[columnIndex] : undefined;
    const isCentered = colParams?.align === "center";

    return (
      <TableCell
        component="div"
        className={clsx(
          classes.tableCell,
          classes.flexContainer,
          classes.maxWidth100,
          classes.noClick,
          level === 1 && classes.level1,
          level === 2 && classes.level2,
          level === 3 && mapping && classes.level3,
          level === 3 && !mapping && classes.level3NotMapped
        )}
        variant="body"
        style={{ height: rowHeight }}
        align={colParams?.align ?? "left"}
      >
        <div
          className={clsx(
            classes.maxWidth100,
            isCentered && classes.marginAuto
          )}
        >
          {colParams?.cellContentRenderer
            ? colParams.cellContentRenderer(params)
            : cellData}
        </div>
      </TableCell>
    );
  };

  headerRenderer = ({
    label,
    columnIndex
  }: TableHeaderProps & { columnIndex: number }) => {
    const { headerHeight, columns, classes, sortState, setSortState } =
      this.props;

    const colParams = columnIndex != null ? columns[columnIndex] : undefined;
    const isCentered = colParams?.align === "center";

    const { column: sortColumn, desc } = sortState;
    const sorted = sortColumn === colParams?.sortKey;

    return (
      <TableCell
        component="div"
        className={clsx(
          classes.tableCell,
          classes.flexContainer,
          classes.noClick
        )}
        variant="head"
        style={{ height: headerHeight }}
        align={colParams?.align ?? "left"}
      >
        <div className={clsx(isCentered && classes.marginAuto)}>
          <TableSortLabel
            active={sorted}
            direction={sorted && desc ? "desc" : "asc"}
            disabled={colParams?.sortKey == null}
            onClick={() => {
              if (colParams?.sortKey) {
                setSortState({
                  column: colParams.sortKey,
                  desc: sorted ? !desc : false
                });
              }
            }}
          >
            <span>{label}</span>
          </TableSortLabel>
        </div>
      </TableCell>
    );
  };

  noRowsRenderer = () => {
    const { loading } = this.props;
    return loading ? (
      <LinearProgress />
    ) : (
      <EmptyList text="กรุณาเลือก Partner ที่มีข้อมูลหมวดสินค้า" />
    );
  };

  render() {
    const {
      classes,
      columns,
      loading,
      rowHeight,
      headerHeight,
      ...tableProps
    } = this.props;

    return (
      <AutoSizer>
        {({ height: maxHeight, width: maxWidth }) => (
          <Table
            height={maxHeight}
            width={maxWidth}
            rowHeight={rowHeight}
            headerHeight={headerHeight}
            gridStyle={{ direction: "inherit" }}
            {...tableProps}
            rowClassName={clsx(classes.tableRow, classes.flexContainer)}
            noRowsRenderer={this.noRowsRenderer}
          >
            {columns.map(({ dataKey, width, ...other }, index) => {
              return (
                <Column
                  key={dataKey}
                  headerRenderer={headerProps =>
                    this.headerRenderer({
                      ...headerProps,
                      columnIndex: index
                    })
                  }
                  className={classes.flexContainer}
                  cellRenderer={this.cellRenderer}
                  width={typeof width === "function" ? width(maxWidth) : width}
                  dataKey={dataKey}
                  {...other}
                />
              );
            })}
          </Table>
        )}
      </AutoSizer>
    );
  }
}

export const VirtualizedCategoryTreeTable = withStyles(styles)(
  VirtualizedCategoryTreeTableComponent
);
